import React, { createContext, useContext, useState, useCallback } from 'react';
import { partnerService } from '../../services/partnerManagement/partnerService';
import { toast } from 'react-hot-toast';

const PartnerContext = createContext();

export const usePartner = () => useContext(PartnerContext);

export const PartnerProvider = ({ children }) => {
  const [locations, setLocations] = useState([]);
  const [partners, setPartners] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Eager fetching on root mount removed.
  // Partner data will be fetched on-demand when PartnerManagementPage mounts.

  const fetchLocations = useCallback(async () => {
    const token = localStorage.getItem('token_agents');
    if (!token) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const data = await partnerService.getLocations();
      // Adjust according to the API response structure. Assuming the locations are inside data or data.data
      setLocations(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error('Failed to fetch locations', error);
      toast.error('Failed to fetch locations');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchPartners = useCallback(async () => {
    const token = localStorage.getItem('token_agents');
    if (!token) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const data = await partnerService.getPartners();
      setPartners(data?.partners || []);
    } catch (error) {
      console.error('Failed to fetch partners', error);
      toast.error('Failed to fetch partners');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const uploadImages = async (files) => {
    try {
      const formData = new FormData();
      Array.from(files).forEach((file) => {
        formData.append('files', file); // Backend expects 'files'
      });
      const response = await partnerService.uploadMultipleImages(formData);
      return response.fileUrls || response.urls || [];
    } catch (error) {
      console.error('Failed to upload images', error);
      toast.error('Failed to upload images');
      throw error;
    }
  };

  const registerPartner = async (partnerData, onSuccess) => {
    try {
      setIsSubmitting(true);
      const response = await partnerService.registerPartner(partnerData);
      toast.success(response.message || 'Partner registered successfully');
      fetchPartners(); // Refresh list after adding
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Registration failed', error);
      toast.error(error.message || 'Failed to register partner');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updatePartner = async (id, partnerData, onSuccess) => {
    try {
      setIsSubmitting(true);
      const response = await partnerService.updatePartner(id, partnerData);
      toast.success(response.message || 'Partner updated successfully');
      fetchPartners();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Update failed', error);
      toast.error(error.message || 'Failed to update partner');
    } finally {
      setIsSubmitting(false);
    }
  };

  const freezePartner = async (id, category) => {
    try {
      const response = await partnerService.freezePartner(id, category);
      toast.success(response.message || 'Partner frozen status updated');
      fetchPartners();
    } catch (error) {
      console.error('Freeze failed', error);
      toast.error(error.message || 'Failed to freeze partner');
    }
  };

  const deletePartner = async (id, category) => {
    try {
      const response = await partnerService.deletePartner(id, category);
      toast.success(response.message || 'Partner deleted successfully');
      fetchPartners();
    } catch (error) {
      console.error('Delete failed', error);
      toast.error(error.message || 'Failed to delete partner');
    }
  };

  const value = {
    locations,
    partners,
    isLoading,
    isSubmitting,
    fetchPartners,
    fetchLocations,
    uploadImages,
    registerPartner,
    updatePartner,
    freezePartner,
    deletePartner
  };

  return (
    <PartnerContext.Provider value={value}>
      {children}
    </PartnerContext.Provider>
  );
};
